import "@/App.css";
import { useEffect, useMemo } from "react";
import { BrowserRouter, Routes, Route, useParams, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { useBucket } from "@/hooks/useBucket";
import { useGeo } from "@/hooks/useGeo";
import { AuthProvider, useAuth, login } from "@/hooks/useAuth";
import { api, visitorId } from "@/lib/api";
import { Header } from "@/components/Header";
import { TravelSection } from "@/components/travel/TravelSection";
import { LifeList } from "@/components/life/LifeList";
import { AuthCallback } from "@/components/auth/AuthCallback";
import Landing from "@/pages/Landing";
import { FriendsSection } from "@/components/friends/FriendsSection";
import { TopFive } from "@/components/top5/TopFive";
import { MakeOwnFloating } from "@/components/friends/FollowButton";
import { FriendHero } from "@/components/friends/FriendHero";
import { WelcomeTour } from "@/components/WelcomeTour";
import AdminTrips from "@/pages/AdminTrips";
import SwapPage from "@/pages/SwapPage";
import TapPage from "@/pages/TapPage";
import { PreviewWarning } from "@/components/PreviewWarning";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { BottomBar } from "@/components/dock/BottomBar";

const READ_ONLY = { readOnly: true };

const Loading = () => <div className="h-screen grid place-items-center bg-[#0B0F17] font-mono text-xs text-slate-500" data-testid="app-loading">Loading your adventures…</div>;

const OwnerPage = () => {
  const b = useBucket();
  return <BucketPage data={b.data} b={b} />;
};

const Home = () => {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  return user ? <OwnerPage /> : <Landing />;
};

const SharedPage = () => {
  const { token } = useParams();
  const { data, isError } = useQuery({ queryKey: ["public", token], queryFn: () => api.publicState(token), retry: false });
  useEffect(() => {
    api.visit({ token, s: new URLSearchParams(window.location.search).get("s") || "", visitor: visitorId() });
  }, [token]);
  if (isError)
    return <div className="h-screen grid place-items-center bg-[#0B0F17] text-slate-400 text-sm" data-testid="share-invalid">This share link is not valid anymore.</div>;
  return <BucketPage data={data} b={READ_ONLY} />;
};

const FriendPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { data, error } = useQuery({ queryKey: ["friend", id], queryFn: () => api.friend(id), retry: false, enabled: !!user });
  const b = useMemo(() => ({ readOnly: true, friendId: id }), [id]);
  useEffect(() => window.scrollTo(0, 0), [id]);
  if (user === undefined) return <Loading />;
  if (!user) return <Landing />;
  if (error)
    return (
      <div className="h-screen grid place-items-center bg-[#0B0F17] text-slate-400 text-sm text-center px-6" data-testid="friend-page-error">
        <div>{error?.response?.data?.detail || "You can only see the lists of your friends."}<br /><a href="/#friends" className="text-sky-300 mt-3 inline-block">Back to my bucket list</a></div>
      </div>
    );
  return <BucketPage data={data} b={b} />;
};

// Story "Make my own" link: straight to Google sign-in, crediting the sharer
const JoinPage = () => {
  const { token } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (user === undefined) return;
    if (user) {
      api.requestByLink(token).catch(() => null).finally(() => navigate("/", { replace: true }));
      return;
    }
    api.visit({ token, s: new URLSearchParams(window.location.search).get("s") || "", visitor: visitorId() });
    login(token);
  }, [user, token, navigate]);
  return <div className="h-screen grid place-items-center bg-[#0B0F17] font-mono text-xs text-slate-500" data-testid="join-page">Taking you to sign in…</div>;
};

const AdminPage = () => {
  const { user } = useAuth();
  if (user === undefined) return <Loading />;
  return user ? <AdminTrips /> : <Landing />;
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/share/:token" element={<SharedPage />} />
      <Route path="/friend/:id" element={<FriendPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/join/:token" element={<JoinPage />} />
      <Route path="/swap/:token" element={<SwapPage />} />
      <Route path="/tap/:token" element={<TapPage />} />
      <Route path="/:token" element={<SharedPage />} />
    </Routes>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster theme="dark" position="bottom-center" offset={96} mobileOffset={{ bottom: 92 }} richColors style={{ zIndex: 9999, pointerEvents: "auto" }} />
      </AuthProvider>
    </BrowserRouter>
  );
}

function BucketPage({ data, b }) {
  const geo = useGeo();
  return (
    <div id="top" className="min-h-screen bg-[#0B0F17] text-slate-100 grain">
      <Header data={data} readOnly={b.readOnly} friendView={!!b.friendId} />
      {data ? (
        <main>
          <TravelSection geo={geo} data={data} b={b} />
          {b.friendId && <FriendHero friendId={b.friendId} friend={data} />}
          <TopFive data={data} b={b} names={geo?.names || []} />
          <LifeList goals={data.goals} b={b} />
          {!b.readOnly && <FriendsSection me={data} geo={geo} />}
          <footer className={`px-6 sm:px-12 py-10 border-t border-white/5 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between pb-28`} data-testid="site-footer">
            <FeedbackDialog />
            <span className="font-mono text-[11px] text-slate-600">the bucketlist — one life, one map.</span>
          </footer>
        </main>
      ) : (
        <div className="h-screen grid place-items-center font-mono text-xs text-slate-500" data-testid="app-loading">Loading your adventures…</div>
      )}
      {b.readOnly && !b.friendId && <MakeOwnFloating />}
      {!b.readOnly && data && <BottomBar data={data} b={b} geo={geo} />}
      {!b.readOnly && <PreviewWarning />}
      {!b.readOnly && <WelcomeTour data={data} b={b} names={geo?.names || []} />}
    </div>
  );
}
